import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, Typography, Paper, Box, Grid, Card, 
  CardContent, CircularProgress, Alert, FormControl,
  InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Chip, Button, Collapse
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { 
  getProjectGanttChart, 
  getTaskStatusChart,
  ChartData
} from '../api/projectAnalyticsService';
import { getProjects, Project } from '../api/projectService';
import { getTasksByProjectId, Task } from '../api/taskService';

interface ProjectStatistics {
  totalTasks: number;
  completedTasks: number;
  completionRate: number;
  overdueTasks: number;
  averageTaskScore: number;
}

interface PriorityScores {
  Critical: number;
  High: number;
  Medium: number;
  Low: number;
  [key: string]: number;
}

interface StatusScores {
  'Blocked': number;
  'In Progress': number;
  'To Do': number;
  'Done': number;
  [key: string]: number;
}

const ProjectVisualizerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialProjectId = searchParams.get('projectId');
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId || '');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [ganttChart, setGanttChart] = useState<ChartData | null>(null);
  const [taskChart, setTaskChart] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  // Load projects on mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsData = await getProjects();
        setProjects(projectsData);
        
        // Auto-select first project if no project is selected
        if (projectsData.length > 0 && !selectedProjectId) {
          const firstProjectId = projectsData[0].id?.toString() || '';
          setSelectedProjectId(firstProjectId);
          setSearchParams({ projectId: firstProjectId });
        }
      } catch (err: any) {
        setError(`Failed to load projects: ${err.message}`);
      }
    };

    loadProjects();
  }, [selectedProjectId, setSearchParams]);

  const calculateTaskScore = useCallback((task: Task): number => {
    const priorityScores: PriorityScores = { 
      'Critical': 40, 
      'High': 30, 
      'Medium': 20, 
      'Low': 10 
    };
    
    const statusScores: StatusScores = { 
      'Blocked': 30, 
      'In Progress': 20, 
      'To Do': 10, 
      'Done': 0 
    };
    
    const priorityScore = priorityScores[task.priority] || 0;
    const statusScore = statusScores[task.status] || 0;
    
    let score = priorityScore + statusScore;
    
    if (task.due_date && task.status !== 'Done') {
      const daysUntilDue = Math.ceil((new Date(task.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysUntilDue < 0) score += 30;
      else if (daysUntilDue <= 3) score += 25;
      else if (daysUntilDue <= 7) score += 15;
      else if (daysUntilDue <= 14) score += 5;
    }
    
    return Math.min(score, 100);
  }, []);

  const statistics: ProjectStatistics | null = tasks.length > 0 ? {
    totalTasks: tasks.length,
    completedTasks: tasks.filter(t => t.status === 'Done').length,
    completionRate: Math.round((tasks.filter(t => t.status === 'Done').length / tasks.length) * 1000) / 10,
    overdueTasks: tasks.filter(t => t.status !== 'Done' && t.due_date && new Date(t.due_date) < new Date()).length,
    averageTaskScore: Math.round(tasks.reduce((sum, task) => sum + calculateTaskScore(task), 0) / tasks.length * 10) / 10
  } : null;

  // Main data fetch effect
  const fetchData = useCallback(async () => {
    if (!selectedProjectId) return;
    
    try {
      setLoading(true);
      setError(null);
      const projectId = parseInt(selectedProjectId);
      setSearchParams({ projectId: selectedProjectId });

      // Find and set the selected project
      const project = projects.find(p => p.id === projectId);
      setSelectedProject(project || null);
      
      // Fetch all data in parallel
      const [tasksData, ganttData, taskChartData] = await Promise.all([
        getTasksByProjectId(projectId).catch(err => {
          console.error('Error fetching tasks:', err);
          return [];
        }),
        getProjectGanttChart(projectId).catch(err => {
          console.error('Error fetching project Gantt chart:', err);
          return null;
        }),
        getTaskStatusChart(projectId).catch(err => {
          console.error('Error fetching task chart:', err);
          return null;
        })
      ]);
      
      // Set all state
      setTasks(tasksData);
      setGanttChart(ganttData);
      setTaskChart(taskChartData);
      
      if (ganttData) {
        setDebugInfo({
          ganttData: {
            dataLength: ganttData.data?.length || 0
          }
        });
      }
      
    } catch (err: any) {
      setError(`Failed to load project data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, projects, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getScoreColor = (score: number) => 
    score >= 70 ? '#f44336' : score >= 40 ? '#ff9800' : '#4caf50';

  const handleProjectChange = (event: any) => {
    const newProjectId = event.target.value;
    setSelectedProjectId(newProjectId);
    setTasks([]);
    setGanttChart(null);
    setTaskChart(null);
    setDebugInfo(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'On Hold': return 'warning';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'Done': return 'success';
      case 'In Progress': return 'primary';
      case 'Blocked': return 'error';
      default: return 'default';
    }
  };

  const getTaskPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  // Helper to check if Gantt chart has valid data
  const hasValidGanttData = (chart: ChartData | null): boolean => {
    if (!chart || !chart.data || !Array.isArray(chart.data)) return false;
    
    // Check if any trace has valid x or y data
    return chart.data.some(trace => 
      (trace.x && Array.isArray(trace.x) && trace.x.length > 0) ||
      (trace.y && Array.isArray(trace.y) && trace.y.length > 0) ||
      (trace.base && Array.isArray(trace.base) && trace.base.length > 0)
    );
  };

  // Calculate dynamic height based on total items across all traces
  const calculateChartHeight = (chart: ChartData | null) => {
    if (!chart || !chart.data || !Array.isArray(chart.data)) return 400;
    
    // Count total unique y-axis items across all traces
    const uniqueItems = new Set();
    chart.data.forEach(trace => {
      if (trace.y && Array.isArray(trace.y)) {
        trace.y.forEach((item: any) => uniqueItems.add(item));
      }
    });
    
    const itemCount = uniqueItems.size || 0;
    return Math.max(400, itemCount * 40 + 150);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Zandrigo Visualizer</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Detailed analytics and visualization for individual projects
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel>Select Project</InputLabel>
          <Select 
            value={selectedProjectId} 
            onChange={handleProjectChange} 
            disabled={loading}
            label="Select Project"
          >
            {projects.map(project => (
              <MenuItem key={project.id} value={project.id?.toString() || ''}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}

      {selectedProject && !loading && (
        <>
          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>{selectedProject.name}</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {selectedProject.description}
            </Typography>
            
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid {...{ item: true, xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip 
                      label={selectedProject.status} 
                      size="small" 
                      color={getStatusColor(selectedProject.status) as any} 
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid {...{ item: true, xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Priority
                    </Typography>
                    <Chip 
                      label={selectedProject.priority} 
                      size="small" 
                      color={getPriorityColor(selectedProject.priority) as any} 
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid {...{ item: true, xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Timeline
                    </Typography>
                    <Typography variant="body2">
                      {selectedProject.start_date ? new Date(selectedProject.start_date).toLocaleDateString() : 'No start date'} - {' '}
                      {selectedProject.target_end_date ? new Date(selectedProject.target_end_date).toLocaleDateString() : 'No end date'}
                    </Typography>
                    {(!selectedProject.start_date || !selectedProject.target_end_date) && (
                      <Alert severity="warning" sx={{ mt: 1 }} icon={false}>
                        <Typography variant="caption">
                          ⚠️ Missing date values - Gantt chart requires both start and end dates
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid {...{ item: true, xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Budget
                    </Typography>
                    <Typography variant="body2">
                      {selectedProject.budget ? `$${selectedProject.budget.toLocaleString()}` : 'Not specified'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {statistics && tasks.length > 0 && (
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid {...{ item: true, xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4" color="primary.main" sx={{ mb: 1 }}>
                      {statistics.totalTasks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Tasks
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid {...{ item: true, xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4" color="success.main" sx={{ mb: 1 }}>
                      {statistics.completedTasks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid {...{ item: true, xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4" color="primary.main" sx={{ mb: 1 }}>
                      {statistics.completionRate}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completion Rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid {...{ item: true, xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4" color="warning.main" sx={{ mb: 1 }}>
                      {statistics.overdueTasks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Overdue
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid {...{ item: true, xs: 6, sm: 4, md: 2.4 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4" sx={{ color: getScoreColor(statistics.averageTaskScore), mb: 1 }}>
                      {statistics.averageTaskScore}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Risk Score
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Enhanced Charts Section */}
          <Box sx={{ display: 'flex', gap: 4, mb: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
            {/* Gantt Chart - 70% width */}
            <Paper elevation={2} sx={{ p: 3, flex: { xs: 1, lg: 0.7 }, minHeight: 550 }}>
              <Typography variant="h5" gutterBottom>
                Task Timeline - {selectedProject.name}
              </Typography>
              
              {hasValidGanttData(ganttChart) ? (
                <Box>
                  <Plot 
                    data={ganttChart!.data} 
                    layout={{ 
                      ...ganttChart!.layout,
                      title: `Task Timeline - ${selectedProject.name}`,
                      height: calculateChartHeight(ganttChart),
                      margin: { l: 150, r: 50, t: 80, b: 100 },
                      xaxis: {
                        ...ganttChart!.layout?.xaxis,
                        type: 'date',
                        tickformat: '%b %d, %Y',
                        rangeslider: { visible: false }
                      },
                      yaxis: {
                        ...ganttChart!.layout?.yaxis,
                        type: 'category',
                        automargin: true
                      },
                      showlegend: true,
                      plot_bgcolor: '#fafafa',
                      paper_bgcolor: '#ffffff'
                    }} 
                    style={{ width: '100%', height: `${calculateChartHeight(ganttChart)}px` }}
                    useResizeHandler 
                    config={{
                      displayModeBar: true,
                      displaylogo: false,
                      responsive: true
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  height: 500, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <Typography variant="h6" color="text.secondary">
                    No timeline data available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: '80%' }}>
                    {!selectedProject.start_date || !selectedProject.target_end_date ? (
                      `This project is missing required date values. 
                       Please set both Start Date and Target End Date for the project to display the Gantt chart.`
                    ) : (
                      'No task data available for this project.'
                    )}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => setShowDebug(!showDebug)}
                    sx={{ mt: 1 }}
                  >
                    {showDebug ? 'Hide Debug Info' : 'Show Debug Info'}
                  </Button>
                  
                  <Collapse in={showDebug}>
                    <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5', maxWidth: '100%', overflow: 'auto' }}>
                      <Typography variant="subtitle2" gutterBottom>Debug Information:</Typography>
                      <pre style={{ fontSize: '11px', margin: 0 }}>
                        {JSON.stringify({
                          project: {
                            id: selectedProject.id,
                            name: selectedProject.name,
                            start_date: selectedProject.start_date,
                            target_end_date: selectedProject.target_end_date,
                            hasValidDates: !!(selectedProject.start_date && selectedProject.target_end_date)
                          },
                          ganttChart: {
                            received: !!ganttChart,
                            hasData: !!ganttChart?.data,
                            dataLength: ganttChart?.data?.length || 0,
                            traceInfo: ganttChart?.data?.map((t: any, i: number) => ({
                              traceIndex: i,
                              type: t.type,
                              xLength: t.x?.length || 0,
                              yLength: t.y?.length || 0
                            })) || []
                          },
                          tasks: {
                            total: tasks.length,
                            hasDueDates: tasks.filter(t => t.due_date).length
                          }
                        }, null, 2)}
                      </pre>
                    </Paper>
                  </Collapse>
                </Box>
              )}
            </Paper>

            {/* Task Distribution Chart - 30% width */}
            <Paper elevation={2} sx={{ p: 3, flex: { xs: 1, lg: 0.3 }, minHeight: 550 }}>
              <Typography variant="h5" gutterBottom>
                Task Status Distribution
              </Typography>
              {taskChart && taskChart.data && taskChart.data.length > 0 ? (
                <Plot 
                  data={taskChart.data} 
                  layout={{ 
                    ...taskChart.layout, 
                    height: 450,
                    title: `Task Status - ${selectedProject.name}`,
                    autosize: true
                  }} 
                  style={{ width: '100%', height: '450px' }}
                  useResizeHandler 
                  config={{
                    displayModeBar: true,
                    displaylogo: false,
                    responsive: true
                  }}
                />
              ) : (
                <Box sx={{ 
                  height: 450, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Typography color="text.secondary">
                    {tasks.length === 0 ? 'No tasks found for this project' : 'Task distribution data not available'}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Tasks Table */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Project Tasks - {selectedProject.name}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Task Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Risk Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map(task => {
                    const score = calculateTaskScore(task);
                    return (
                      <TableRow key={task.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{task.title}</Typography>
                          {task.description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {task.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={task.status} 
                            size="small" 
                            color={getTaskStatusColor(task.status) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={task.priority} 
                            size="small" 
                            color={getTaskPriorityColor(task.priority) as any}
                          />
                        </TableCell>
                        <TableCell>
                          {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                        </TableCell>
                        <TableCell>
                          <Box 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              bgcolor: getScoreColor(score), 
                              color: 'white', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              fontWeight: 'bold', 
                              fontSize: '0.875rem' 
                            }}
                          >
                            {score}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {tasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">No tasks found for this project</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {!selectedProjectId && !loading && (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Please select a project to view its visualization
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default ProjectVisualizerPage;